/**
 * The plan view of the radial stockyard as SVG: the pivot at the origin, north up, east right,
 * one SVG unit = one metre. Bearings are compass bearings (0° north, clockwise). An arc segment
 * runs from one bearing to another in the stacker's direction (+1 clockwise, −1 counter-clockwise).
 */

import { wrap360 } from "../../lib/units";

const RAD = Math.PI / 180;

export interface Point {
  x: number;
  y: number;
}

/** The point at `bearing`, `r` metres from the pivot (SVG y grows downwards, so north is −y). */
export function polar(bearingDeg: number, r: number): Point {
  return { x: r * Math.sin(bearingDeg * RAD), y: -r * Math.cos(bearingDeg * RAD) };
}

/** The compass bearing of a point (the inverse of `polar`). */
export function bearingOf(p: Point): number {
  return wrap360(Math.atan2(p.x, -p.y) / RAD);
}

/** Degrees from `from` to `to` travelling in `direction`, [0, 360). */
export function sweepDeg(from: number, to: number, direction: 1 | -1): number {
  return wrap360((to - from) * direction);
}

const f = (v: number) => (Math.abs(v) < 1e-9 ? "0" : v.toFixed(3));

/**
 * The closed SVG path of the annular wedge between radii `rIn` and `rOut`, from bearing `from`
 * to bearing `to` in `direction`. SVG's sweep flag 1 draws clockwise on screen, which with north
 * up is increasing bearing.
 */
export function wedgePath(from: number, to: number, rIn: number, rOut: number, direction: 1 | -1): string {
  const span = sweepDeg(from, to, direction);
  const large = span > 180 ? 1 : 0;
  const cw = direction === 1 ? 1 : 0;
  const a = polar(from, rOut);
  const b = polar(to, rOut);
  const c = polar(to, rIn);
  const d = polar(from, rIn);
  return [
    `M ${f(a.x)} ${f(a.y)}`,
    `A ${f(rOut)} ${f(rOut)} 0 ${large} ${cw} ${f(b.x)} ${f(b.y)}`,
    `L ${f(c.x)} ${f(c.y)}`,
    `A ${f(rIn)} ${f(rIn)} 0 ${large} ${1 - cw} ${f(d.x)} ${f(d.y)}`,
    "Z",
  ].join(" ");
}

/** A radial line at `bearing` from `rIn` to `rOut`. */
export function spoke(bearing: number, rIn: number, rOut: number): [Point, Point] {
  return [polar(bearing, rIn), polar(bearing, rOut)];
}

/** The first point of an SVG path (`M x y …`). */
export function pathStart(d: string): Point {
  const m = /^M\s+(-?[\d.]+)\s+(-?[\d.]+)/.exec(d);
  if (!m) throw new Error(`not a path: ${d.slice(0, 20)}`);
  return { x: Number(m[1]), y: Number(m[2]) };
}

/** The end point of the path's first arc command (the outer arc's far end). */
export function firstArcEnd(d: string): Point {
  const m = /A\s+[\d.]+\s+[\d.]+\s+0\s+[01]\s+[01]\s+(-?[\d.]+)\s+(-?[\d.]+)/.exec(d);
  if (!m) throw new Error("no arc in the path");
  return { x: Number(m[1]), y: Number(m[2]) };
}

/** The middle bearing of an arc segment (for labels). */
export function midBearing(from: number, to: number, direction: 1 | -1): number {
  return wrap360(from + (direction * sweepDeg(from, to, direction)) / 2);
}

/** The bounding box of the arc segment between radii 0 (the pivot) and `r`, sampled every 2°. */
export function arcBox(from: number, to: number, r: number, direction: 1 | -1): { x: number; y: number; w: number; h: number } {
  const span = sweepDeg(from, to, direction);
  const pts: Point[] = [{ x: 0, y: 0 }];
  for (let a = 0; a <= span; a += 2) pts.push(polar(from + direction * a, r));
  pts.push(polar(to, r));
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

/** An open arc at radius `r` from bearing `from` to `to` in `direction` (range rings). */
export function arcPath(from: number, to: number, r: number, direction: 1 | -1): string {
  const span = sweepDeg(from, to, direction);
  const a = polar(from, r);
  const b = polar(to, r);
  return `M ${f(a.x)} ${f(a.y)} A ${f(r)} ${f(r)} 0 ${span > 180 ? 1 : 0} ${direction === 1 ? 1 : 0} ${f(b.x)} ${f(b.y)}`;
}
